// context/AuthContext.tsx
// src/context/AuthContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import config from '../config'
import LoginForm from '../components/LoginForm'

interface User {
  id: string
  email: string
  first_name?: string
  last_name?: string
  name?: string // Keep for backward compatibility
  accessToken: string
  tokenType: string
  preferences?: UserPreferencesData
  profile_picture?: string
  is_superuser?: boolean
}

// Dietary preferences are now an array of strings, no longer a boolean object

interface UserHealthConcern {
  id: string;
  health_concern_id?: string;
  is_active: boolean;
  health_concern: {
    id: string;
    name: string;
    display_name?: string;
    description?: string;
    is_active?: boolean;
  };
  notes?: string;
}

interface UserIngredientPreference {
  id: number;
  ingredient_id: number;
  preference_type: 'love' | 'like' | 'dislike' | 'avoid' | 'allergy' | 'intolerance';
  ingredient: {
    id: number;
    name: string;
  };
}

interface UserFavorite {
  id: string;
  item_id: string;
  created_at: string;
  updated_at: string;
  item: {
    id: string;
    name: string;
    normalized_name: string;
    type: 'food' | 'drink';
    approved: boolean;
  };
}

interface UserPreferencesData {
  dietary: string[]; // Array of dietary preference strings
  healthConcerns: UserHealthConcern[];
  ingredientPreferences: UserIngredientPreference[];
  favorites?: UserFavorite[];
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>
  logout: () => void
  getUserPreferences: () => Promise<string[] | null>
  saveUserPreferences: (preferences: string[]) => Promise<boolean>
  showLoginForm: (mode?: 'login' | 'register') => void
  hideLoginForm: () => void
  // New localStorage functions for preferences
  getUserPreferencesFromStorage: () => UserPreferencesData | null
  saveUserPreferencesToStorage: (preferences: UserPreferencesData) => void
  clearUserPreferencesFromStorage: () => void
  // Password reset functions
  requestPasswordReset: (email: string) => Promise<void>
  confirmPasswordReset: (token: string, newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [showLoginForm, setShowLoginForm] = useState(false)
  const [loginFormMode, setLoginFormMode] = useState<'login' | 'register'>('login')
  const navigate = useNavigate()

  const isJwtExpired = (jwtToken: string): boolean => {
    try {
      const payloadBase64 = jwtToken.split('.')[1]
      if (!payloadBase64) return false
      const payloadJson = atob(payloadBase64)
      const payload = JSON.parse(payloadJson) as { exp?: number }
      if (!payload.exp) return false
      const nowInSeconds = Math.floor(Date.now() / 1000)
      return payload.exp <= nowInSeconds
    } catch {
      // If we cannot parse, assume not expired to avoid unnecessary logouts
      return false
    }
  }

  // Rehydrate user + token from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('auth_user')
    if (stored) {
      try {
        const parsed: User = JSON.parse(stored)
        // Only treat as authenticated if we have a token value and it's not expired
        if (parsed?.accessToken) {
          if (!isJwtExpired(parsed.accessToken)) {
            setUser(parsed)
            axios.defaults.headers.common['Authorization'] =
              `${parsed.tokenType.charAt(0).toUpperCase()}${parsed.tokenType.slice(1)} ${parsed.accessToken}`
          } else {
            localStorage.removeItem('auth_user')
          }
        } else {
          localStorage.removeItem('auth_user')
        }
      } catch {
        localStorage.removeItem('auth_user')
      }
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    try {
      
      // 1) Request JWT + user profile + preferences in single call
      const { data } = await axios.post<{
        access_token: string
        token_type: string
        user: Omit<User, 'accessToken' | 'tokenType' | 'preferences'>
        preferences: UserPreferencesData
      }>(`${config.API_BASE_URL}/auth/login`, { email, password })

      const { access_token, token_type, user: profile, preferences } = data


      // 2) Set axios default Authorization header
      axios.defaults.headers.common['Authorization'] =
        `${token_type.charAt(0).toUpperCase()}${token_type.slice(1)} ${access_token}`

      // 3) Merge profile + token + preferences into one object
      const authUser: User = {
        ...profile,
        accessToken: access_token,
        tokenType: token_type,
        preferences: preferences
      }


      // 4) Persist in state & localStorage
      setUser(authUser)
      localStorage.setItem('auth_user', JSON.stringify(authUser))

    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }, [])

  const register = useCallback(async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      
      const requestBody = { 
        email, 
        password, 
        first_name: firstName, 
        last_name: lastName 
      };
      
      
      // 1) Request JWT + user profile + preferences in single call
      const { data } = await axios.post<{
        access_token: string
        token_type: string
        user: Omit<User, 'accessToken' | 'tokenType' | 'preferences'>
        preferences: UserPreferencesData
      }>(`${config.API_BASE_URL}/auth/register`, requestBody)

      const { access_token, token_type, user: profile, preferences } = data


      // 2) Set axios default Authorization header
      axios.defaults.headers.common['Authorization'] =
        `${token_type.charAt(0).toUpperCase()}${token_type.slice(1)} ${access_token}`

      // 3) Merge profile + token + preferences into one object
      const authUser: User = {
        ...profile,
        accessToken: access_token,
        tokenType: token_type,
        preferences: preferences
      }


      // 4) Persist in state & localStorage
      setUser(authUser)
      localStorage.setItem('auth_user', JSON.stringify(authUser))

    } catch (error) {
      console.error('Registration failed:', error)
      throw error
    }
  }, [])

  const logout = useCallback(() => {
    setUser(null)
    localStorage.removeItem('auth_user')
    delete axios.defaults.headers.common['Authorization']
    navigate('/')
  }, [navigate])

  const getUserPreferences = useCallback(async (): Promise<string[] | null> => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/user/my-preferences`)
      return response.data
    } catch (error) {
      console.error('Failed to get user preferences:', error)
      return null
    }
  }, [])

  const saveUserPreferences = useCallback(async (dietaryPreferences: string[]): Promise<boolean> => {
    try {
      const response = await axios.post(`${config.API_BASE_URL}/user/my-preferences-array`, {
        dietary: dietaryPreferences
      }, {
        headers: {
          'Authorization': `Bearer ${user?.accessToken}`,
          'Content-Type': 'application/json'
        }
      })
      return response.status === 200
    } catch (error) {
      console.error('Failed to save user preferences:', error)
      return false
    }
  }, [user?.accessToken])

  const showLoginFormHandler = useCallback((mode: 'login' | 'register' = 'login') => {
    setLoginFormMode(mode)
    setShowLoginForm(true)
  }, [])

  const hideLoginFormHandler = useCallback(() => {
    setShowLoginForm(false)
  }, [])

  // localStorage functions for user preferences
  const getUserPreferencesFromStorage = useCallback((): UserPreferencesData | null => {
    if (!user) return null
    return user.preferences || null
  }, [user])

  const saveUserPreferencesToStorage = useCallback((preferences: UserPreferencesData) => {
    if (!user) return
    
    try {
      const updatedUser = { ...user, preferences }
      setUser(updatedUser)
      localStorage.setItem('auth_user', JSON.stringify(updatedUser))
    } catch (error) {
      console.error('Failed to save user preferences to localStorage:', error)
    }
  }, [user])

  const clearUserPreferencesFromStorage = useCallback(() => {
    if (!user) return
    
    try {
      const updatedUser = { ...user, preferences: undefined }
      setUser(updatedUser)
      localStorage.setItem('auth_user', JSON.stringify(updatedUser))
    } catch (error) {
      console.error('Failed to clear user preferences from localStorage:', error)
    }
  }, [user])

  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    try {
      await axios.post(`${config.API_BASE_URL}/auth/user/password-reset-request`, {
        email: email
      })
    } catch (error: any) {
      console.error('Password reset request failed:', error)
      throw new Error(error.response?.data?.detail || 'Failed to send password reset email')
    }
  }, [])

  const confirmPasswordReset = useCallback(async (token: string, newPassword: string): Promise<void> => {
    try {
      await axios.post(`${config.API_BASE_URL}/auth/user/password-reset-confirm`, {
        reset_code: token,
        new_password: newPassword
      })
    } catch (error: any) {
      console.error('Password reset confirmation failed:', error)
      throw new Error(error.response?.data?.detail || 'Failed to reset password')
    }
  }, [])

  const value = useMemo(
    () => ({ 
      user, 
      login, 
      register, 
      logout, 
      getUserPreferences, 
      saveUserPreferences,
      showLoginForm: showLoginFormHandler,
      hideLoginForm: hideLoginFormHandler,
      getUserPreferencesFromStorage,
      saveUserPreferencesToStorage,
      clearUserPreferencesFromStorage,
      requestPasswordReset,
      confirmPasswordReset
    }),
    [user, login, register, logout, getUserPreferences, saveUserPreferences, showLoginFormHandler, hideLoginFormHandler, getUserPreferencesFromStorage, saveUserPreferencesToStorage, clearUserPreferencesFromStorage, requestPasswordReset, confirmPasswordReset]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showLoginForm && createPortal(
        <LoginForm 
          mode={loginFormMode} 
          onClose={hideLoginFormHandler} 
        />,
        document.body
      )}
    </AuthContext.Provider>
  )
}